import {Box, Checkbox, CircularProgress, ListItemText} from '@mui/material'
import PropTypes from 'prop-types'
import {useDispatch, useSelector} from 'react-redux'
import useCategorizedInterestTagsRefData from '../../../../api/getCategorizedInterestTags'
import {
  setCategoryTagName,
  setRemoveSpecificCategoryTagRefData,
  setSpecificCategoryTagRefData,
  setTotalInterestTagCount,
  setSearchedInterestTag
} from '../../../../store/askGivePrism/categorizedInterestTagSlice'
import {useEffect, useState} from 'react'
import {
  StyledButtonContainer,
  StyledCategoryField,
  StyledCircularContainer,
  StyledDivider,
  StyledGridContainer,
  StyledGridItem,
  StyledInterestTagDivContainer,
  StyledLoadingButton,
  StyledMenuItem,
  StyledMoreButton,
  StyledSearchContainer,
  StyledSearchTypography,
  StyledTryAgainButton,
  StyledTypography
} from './CategoryBasedInterestTagPopupStyles'
import paginationLimit from '../../../../configs/paginationLimit.json'
import SearchPanel from '../../../Common/SearchPanel/SearchPanelIndex'
import NoData from '../../../Common/Error/NoDataFound/NoData'
import LoadingError from '../../../Common/Error/DataLoadingError/LoadingError'

const CategoryBasedInterestTagPopup = ({
  interestTagRefData,
  isInterestTagRefDataLoading,
  isInterestTagRefDataLoadingError,
  selectedInterestOptions,
  handleSelectedTags
}) => {
  const dispatch = useDispatch()
  const [interestsData, setInterestsData] = useState(interestTagRefData)
  const specificCategoryInterestTagData = useSelector(
    (state) => state.categorizedInterestTag.specificCategoryTagRefData
  )
  const interestTagSearchTerm = useSelector((state) => state.categorizedInterestTag.searchedInterestTag)
  const categoryTagName = useSelector((state) => state.categorizedInterestTag.categoryTagName)
  const interestTagCount = useSelector((state) => state.categorizedInterestTag.categoryInterestTagCount)
  const [
    getCategorizedInterestTagsRefData,
    {interestRefData, isInterestRefDataLoading, interestRefDataError, refetch}
  ] = useCategorizedInterestTagsRefData(categoryTagName, interestTagCount, interestTagSearchTerm)
  const [seeMore, setSeeMore] = useState(false)

  useEffect(() => {
    if (interestTagRefData?.length > 0) {
      const updatedInterestsData = updateSelectedTags(interestTagRefData, selectedInterestOptions)
      setInterestsData(updatedInterestsData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interestTagRefData, isInterestTagRefDataLoading, dispatch])

  useEffect(() => {
    if (selectedInterestOptions.length > 0) {
      const updatedInterestsData = updateSelectedTags(interestsData, selectedInterestOptions)
      setInterestsData(updatedInterestsData) // Update the state with the new data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInterestOptions])

  useEffect(() => {
    if (interestRefData?.categoryTags?.length > 0 && !interestTagSearchTerm) {
      const updatedInterestsCategoryData = structuredClone(interestRefData.categoryTags)
      if (interestTagSearchTerm === '' && !seeMore) {
        const updatedInterestsData = updateSelectedTags(updatedInterestsCategoryData, selectedInterestOptions)
        setInterestsData(updatedInterestsData)
      } else {
        dispatch(setSpecificCategoryTagRefData(updatedInterestsCategoryData[0]))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interestRefData, dispatch, interestTagSearchTerm])

  useEffect(() => {
    if (interestRefData?.categoryTags?.length >= 0 && interestTagSearchTerm) {
      const updatedInterestsCategoryData = structuredClone(interestRefData.categoryTags)
      if (seeMore) {
        dispatch(setSpecificCategoryTagRefData(updatedInterestsCategoryData[0]))
      } else {
        const updatedInterestsData = updateSelectedTags(updatedInterestsCategoryData, selectedInterestOptions)
        setInterestsData(updatedInterestsData)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interestRefData, dispatch, interestTagSearchTerm])

  useEffect(() => {
    if (isInterestTagRefDataLoading && interestRefData?.categoryTags?.length === 0) {
      setInterestsData(interestRefData.categoryTags)
    }
  }, [interestRefData, isInterestTagRefDataLoading])

  useEffect(() => {
    if (categoryTagName) {
      getCategorizedInterestTagsRefData()
    }
  }, [categoryTagName, getCategorizedInterestTagsRefData])

  useEffect(() => {
    if (
      specificCategoryInterestTagData.length > 0 &&
      categoryTagName &&
      !isInterestRefDataLoading &&
      !interestRefDataError
    ) {
      const specificCategoryTagName = specificCategoryInterestTagData.find((obj) => obj.category === categoryTagName)
      if (!specificCategoryTagName) return

      const updateSpecificCategoryTagName = structuredClone(specificCategoryTagName)

      if (selectedInterestOptions.length > 0) {
        updateSelectedInterestTags(updateSpecificCategoryTagName, selectedInterestOptions)
      } else {
        resetSelectedTags(updateSpecificCategoryTagName)
      }
      const result = mergeInterestTags(interestsData, updateSpecificCategoryTagName)
      setSeeMore(false)
      setInterestsData(result)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specificCategoryInterestTagData, isInterestRefDataLoading, interestRefDataError])

  const isTagSelected = (selectedInterestOptions, category, tag) => {
    return selectedInterestOptions.some((item) => item.category === category && item.id === tag.id)
  }

  const updateSelectedTags = (interestsData, selectedInterestOptions) => {
    const updatedInterestsData = structuredClone(interestsData) // Create a deep copy
    for (const category of updatedInterestsData) {
      for (const tag of category.interestTagList) {
        tag.selected = isTagSelected(selectedInterestOptions, category.category, tag)
      }
    }

    return updatedInterestsData
  }

  const updateSelectedInterestTags = (updatedSelectedInterestTag, selectedInterestOptions) => {
    for (const item of selectedInterestOptions) {
      if (updatedSelectedInterestTag.category === item.category) {
        const tag = updatedSelectedInterestTag.interestTagList.find((interestTag) => interestTag.id === item.id)
        if (tag) {
          tag.selected = true
        }
      }
    }
  }

  const resetSelectedTags = (updatedSelectedInterestTag) => {
    for (const tag of updatedSelectedInterestTag.interestTagList) {
      tag.selected = false
    }
  }

  const mergeInterestTags = (interestsData, updatedSelectedInterestTag) => {
    return interestsData.map((obj) => {
      if (updatedSelectedInterestTag.category === obj.category) {
        return {
          ...obj,
          interestTagList: [...obj.interestTagList, ...updatedSelectedInterestTag.interestTagList]
        }
      }
      return obj
    })
  }

  const handleSeeMore = (categoryName) => {
    const selectedCategoryData = specificCategoryInterestTagData.find((obj) => obj.category === categoryName)
    if (selectedCategoryData) {
      const updatedSelectedCategory = structuredClone(selectedCategoryData)
      if (selectedInterestOptions.length > 0) {
        updateSelectedInterestTags(updatedSelectedCategory, selectedInterestOptions)
      } else {
        resetSelectedTags(updatedSelectedCategory)
      }
      const result = mergeInterestTags(interestsData, updatedSelectedCategory)
      setInterestsData(result)
    } else {
      const totalInterestTagCount = interestsData.find((obj) => obj.category === categoryName).totalInterestTagCount
      dispatch(setCategoryTagName(categoryName))
      dispatch(setTotalInterestTagCount(totalInterestTagCount))
      setSeeMore(true)
    }
  }

  const handleSeeLess = (categoryName) => {
    const result = interestsData.map((obj) => {
      if (categoryName === obj.category) {
        return {
          ...obj,
          interestTagList: obj.interestTagList.slice(0, paginationLimit.pagination.cateogryInterestTagLimt)
        }
      }
      return obj
    })
    setInterestsData(result)
  }

  const shouldShowSeeMoreButton = (obj) => {
    return (
      (categoryTagName !== obj.category || !isInterestRefDataLoading) &&
      obj.interestTagList.length !==
        interestsData.find((item) => item.category === obj.category).totalInterestTagCount &&
      !interestRefDataError
    )
  }

  const shouldShowSeeLessButton = (obj) => {
    return (
      obj.interestTagList.length > paginationLimit.pagination.cateogryInterestTagLimt &&
      obj.interestTagList.length ===
        interestsData.find((interestTag) => interestTag.category === obj.category).totalInterestTagCount
    )
  }

  const handleTryAgain = () => {
    refetch()
  }

  const handleInterestTagClick = (categoryName, interestTagId, interestName) => {
    const selectedInterestIndex = selectedInterestOptions.findIndex(
      (item) => item.id === interestTagId && item.category === categoryName
    )
    const checked = selectedInterestIndex !== -1
    if (checked) {
      const updatedInterestsData = structuredClone(interestsData)
      const category = selectedInterestOptions[selectedInterestIndex].category
      const interestId = selectedInterestOptions[selectedInterestIndex].id
      const categoryObj = updatedInterestsData.find((obj) => obj.category === category)
      if (categoryObj) {
        const tag = categoryObj.interestTagList.find((interestTag) => interestTag.id === interestId)
        if (tag) {
          tag.selected = false
        }
      }
      setInterestsData(updatedInterestsData)
    }
    handleSelectedTags(checked, selectedInterestOptions, categoryName, interestTagId, interestName)
  }

  const handleSearchTerm = (searchTerm) => {
    setSeeMore(false)
    dispatch(setRemoveSpecificCategoryTagRefData())
    dispatch(setCategoryTagName(''))
    dispatch(setSearchedInterestTag(searchTerm))
    getCategorizedInterestTagsRefData()
  }

  const interestTagsList = (obj) => {
    return (
      <StyledGridContainer container spacing={2}>
        {obj.interestTagList.map((interestTag) => (
          <StyledGridItem item xs={12} sm={4} key={interestTag.id}>
            <StyledMenuItem
              dense
              onClick={() => handleInterestTagClick(obj.category, interestTag.id, interestTag.name)}
            >
              <Checkbox checked={interestTag.selected || false} />
              <ListItemText
                sx={{textWrap: 'wrap'}}
                primaryTypographyProps={{
                  color: (theme) => theme.palette.common.black
                }}
                primary={interestTag.name}
              />
            </StyledMenuItem>
          </StyledGridItem>
        ))}
      </StyledGridContainer>
    )
  }

  const buttons = (obj) => {
    return (
      <StyledButtonContainer>
        {shouldShowSeeMoreButton(obj) && (
          <StyledMoreButton onClick={() => handleSeeMore(obj.category)}>See more</StyledMoreButton>
        )}
        {categoryTagName === obj.category && isInterestRefDataLoading && (
          <StyledLoadingButton>Loading...</StyledLoadingButton>
        )}
        {categoryTagName === obj.category && !isInterestRefDataLoading && interestRefDataError && (
          <StyledTypography>
            An error occurred. Please <StyledTryAgainButton onClick={handleTryAgain}>try again</StyledTryAgainButton>
          </StyledTypography>
        )}
        {shouldShowSeeLessButton(obj) && (
          <StyledMoreButton onClick={() => handleSeeLess(obj.category)}>See less</StyledMoreButton>
        )}
      </StyledButtonContainer>
    )
  }

  return (
    <div>
      <StyledSearchTypography>Search for a interest</StyledSearchTypography>
      <StyledSearchContainer>
        <Box sx={{width: '50%'}}>
          <SearchPanel
            variant="filled"
            width="100%"
            searchTerm={interestTagSearchTerm}
            placeholder="Start typing..."
            setSearchTerm={handleSearchTerm}
          />
        </Box>
      </StyledSearchContainer>
      {(isInterestTagRefDataLoading || isInterestRefDataLoading) && !seeMore && (
        <StyledCircularContainer>
          <CircularProgress size="40px" />
        </StyledCircularContainer>
      )}
      {!((isInterestTagRefDataLoading || isInterestRefDataLoading) && !seeMore) &&
        (isInterestTagRefDataLoadingError || interestRefDataError) &&
        !seeMore && (
          <StyledCircularContainer>
            <LoadingError height={'59vh'} />
          </StyledCircularContainer>
        )}
      {!((isInterestTagRefDataLoading || isInterestRefDataLoading) && !seeMore) && interestsData?.length === 0 && (
        <StyledCircularContainer>
          <NoData message="No data available" />
        </StyledCircularContainer>
      )}

      {!((isInterestTagRefDataLoading || isInterestRefDataLoading) && !seeMore) &&
        !((isInterestTagRefDataLoadingError || interestRefDataError) && !seeMore) &&
        interestsData?.length > 0 &&
        interestsData?.map((obj) => (
          <StyledInterestTagDivContainer key={obj.category}>
            <StyledCategoryField>{obj.category}</StyledCategoryField>
            <StyledDivider />
            {interestTagsList(obj)}
            {buttons(obj)}
          </StyledInterestTagDivContainer>
        ))}
    </div>
  )
}

CategoryBasedInterestTagPopup.propTypes = {
  interestTagRefData: PropTypes.array,
  isInterestTagRefDataLoading: PropTypes.bool,
  isInterestTagRefDataLoadingError: PropTypes.bool,
  selectedInterestOptions: PropTypes.array,
  handleSelectedTags: PropTypes.func
}

export default CategoryBasedInterestTagPopup
